import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../services/api';

const BusinessPrestations = ({ projectMode = false }) => {
  // Contexte du projet si en mode projet
  const outletContext = useOutletContext();
  const projectId = projectMode ? outletContext?.projectId : null;
  const canManage = projectMode ? outletContext?.canManage : true;
  
  // Base URL de l'API selon le mode
  const apiBase = projectMode && projectId ? `/projects/${projectId}/workspace` : '/business';
  
  const [prestations, setPrestations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showModal, setShowModal] = useState(false);
  const [editingPrestation, setEditingPrestation] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPrestation, setSelectedPrestation] = useState(null);
  const [clients, setClients] = useState([]);
  
  // États pour le cumul avec période
  const [periodType, setPeriodType] = useState('all'); // all, month, quarter, year, custom
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');

  useEffect(() => {
    loadPrestations();
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, projectId]);

  const loadPrestations = async () => {
    try {
      let url = `${apiBase}/prestations`;
      if (filter !== 'all') url += `?statut=${filter}`;
      const res = await api.get(url);
      setPrestations(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const res = await api.get(`${apiBase}/clients`);
      setClients(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette prestation ?')) return;
    try {
      await api.delete(`${apiBase}/prestations/${id}`);
      loadPrestations();
    } catch (err) {
      console.error(err);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await api.put(`${apiBase}/prestations/${id}`, { statut: newStatus });
      loadPrestations();
    } catch (err) {
      console.error(err);
    }
  };

  const getStatusBadge = (statut) => {
    const styles = {
      en_attente: 'bg-gray-100 text-gray-600',
      en_cours: 'bg-blue-100 text-blue-600',
      terminee: 'bg-green-100 text-green-600',
      annulee: 'bg-red-100 text-red-600'
    };
    const labels = {
      en_attente: '⏳ En attente',
      en_cours: '🔄 En cours',
      terminee: '✅ Terminée',
      annulee: '❌ Annulée'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[statut] || styles.en_attente}`}>
        {labels[statut] || statut}
      </span>
    );
  };

  // Fonction pour filtrer par période
  const filterByPeriod = (items) => {
    if (periodType === 'all') return items;
    
    const now = new Date();
    let startDate, endDate;
    
    switch (periodType) {
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'quarter':
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        endDate = new Date(now.getFullYear(), quarter * 3 + 3, 0);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31);
        break;
      case 'custom':
        if (customDateStart && customDateEnd) {
          startDate = new Date(customDateStart);
          endDate = new Date(customDateEnd);
        } else {
          return items;
        }
        break;
      default:
        return items;
    }
    
    return items.filter(p => {
      const dateFinReelle = p.date_fin_reelle ? new Date(p.date_fin_reelle) : null;
      if (!dateFinReelle) return false;
      return dateFinReelle >= startDate && dateFinReelle <= endDate;
    });
  };

  // Calcul du cumul des prestations terminées
  const prestationsTerminees = prestations.filter(p => p.statut === 'terminee');
  const prestationsTermineesFiltered = filterByPeriod(prestationsTerminees);
  const cumulTerminees = prestationsTermineesFiltered.reduce((sum, p) => sum + parseFloat(p.montant_paye || 0), 0);
  const nombreTerminees = prestationsTermineesFiltered.length;

  // Stats
  const stats = {
    total: prestations.length,
    enCours: prestations.filter(p => p.statut === 'en_cours').length,
    enAttente: prestations.filter(p => p.statut === 'en_attente').length,
    terminees: prestations.filter(p => p.statut === 'terminee').length,
    valeurEnCours: prestations
      .filter(p => ['en_cours', 'en_attente'].includes(p.statut))
      .reduce((sum, p) => sum + parseFloat(p.montant || 0), 0),
    resteAEncaisser: prestations
      .filter(p => p.statut !== 'annulee')
      .reduce((sum, p) => sum + (parseFloat(p.montant || 0) - parseFloat(p.montant_paye || 0)), 0)
  };

  if (loading) {
    return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div></div>;
  }

  // Filtrage par recherche
  const filteredPrestations = prestations.filter(prestation => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      prestation.titre?.toLowerCase().includes(searchLower) ||
      prestation.description?.toLowerCase().includes(searchLower) ||
      prestation.client_nom?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredPrestations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPrestations = filteredPrestations.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">Prestations</h1>
          <p className="text-gray-500 text-sm">Suivez vos services et missions en cours</p>
        </div>
        {canManage && (
          <button
            onClick={() => { setEditingPrestation(null); setShowModal(true); }}
            className="px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition flex items-center justify-center gap-2"
          >
            <span>➕</span> Nouvelle prestation
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-purple-500 text-xl md:text-2xl mb-1 md:mb-2">📋</div>
          <div className="text-xl md:text-2xl font-bold text-gray-800">{stats.total}</div>
          <div className="text-xs md:text-sm text-gray-500">Total</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-blue-500 text-xl md:text-2xl mb-1 md:mb-2">🔄</div>
          <div className="text-xl md:text-2xl font-bold text-blue-600">{stats.enCours}</div>
          <div className="text-xs md:text-sm text-gray-500">En cours</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-amber-500 text-xl md:text-2xl mb-1 md:mb-2">⏳</div>
          <div className="text-xl md:text-2xl font-bold text-amber-600">{stats.enAttente}</div>
          <div className="text-xs md:text-sm text-gray-500">En attente</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4">
          <div className="text-green-500 text-xl md:text-2xl mb-1 md:mb-2">💰</div>
          <div className="text-lg md:text-xl font-bold text-green-600">{stats.valeurEnCours.toLocaleString()} <span className="text-xs">XOF</span></div>
          <div className="text-xs md:text-sm text-gray-500">Valeur en cours</div>
        </div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4 col-span-2 sm:col-span-1">
          <div className="text-red-500 text-xl md:text-2xl mb-1 md:mb-2">💳</div>
          <div className="text-lg md:text-xl font-bold text-red-600">{stats.resteAEncaisser.toLocaleString()} <span className="text-xs">XOF</span></div>
          <div className="text-xs md:text-sm text-gray-500">À encaisser</div>
        </div>
      </div>

      {/* Cumul des prestations terminées avec sélecteur de période */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl shadow-lg p-4 md:p-6 mb-6 text-white">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h3 className="text-sm font-medium opacity-90 mb-1">Cumul des prestations terminées</h3>
            <div className="text-2xl md:text-3xl font-bold">
              {cumulTerminees.toLocaleString()} <span className="text-lg opacity-80">XOF</span>
            </div>
            <div className="text-sm opacity-80 mt-1">{nombreTerminees} prestation{nombreTerminees > 1 ? 's' : ''} terminée{nombreTerminees > 1 ? 's' : ''}</div>
          </div>
          
          {/* Sélecteur de période */}
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value)}
              className="px-3 py-2 rounded-lg bg-white/20 border border-white/30 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
            >
              <option value="all" className="text-gray-800">Toute la période</option>
              <option value="month" className="text-gray-800">Ce mois</option>
              <option value="quarter" className="text-gray-800">Ce trimestre</option>
              <option value="year" className="text-gray-800">Cette année</option>
              <option value="custom" className="text-gray-800">Personnalisé</option>
            </select>
            
            {periodType === 'custom' && (
              <div className="flex gap-2">
                <input
                  type="date"
                  value={customDateStart}
                  onChange={(e) => setCustomDateStart(e.target.value)}
                  className="px-2 py-2 rounded-lg bg-white/20 border border-white/30 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
                />
                <input
                  type="date"
                  value={customDateEnd}
                  onChange={(e) => setCustomDateEnd(e.target.value)}
                  className="px-2 py-2 rounded-lg bg-white/20 border border-white/30 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow p-4 mb-6">
        {/* Barre de recherche */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher par titre, description, client..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto">
          {[
            { value: 'all', label: '📋 Toutes' },
            { value: 'en_attente', label: '⏳ En attente' },
            { value: 'en_cours', label: '🔄 En cours' },
            { value: 'terminee', label: '✅ Terminées' },
            { value: 'annulee', label: '❌ Annulées' }
          ].map(f => (
            <button
              key={f.value}
              onClick={() => { setFilter(f.value); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition ${
                filter === f.value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Résultat de recherche */}
      {search && (
        <div className="mb-4 text-sm text-gray-500">
          {filteredPrestations.length} résultat(s) pour "{search}"
        </div>
      )}

      {/* Prestations List */}
      {paginatedPrestations.length > 0 ? (
        <div className="grid gap-4">
          {paginatedPrestations.map(prestation => {
            const montant = parseFloat(prestation.montant || 0);
            const montantPaye = parseFloat(prestation.montant_paye || 0);
            const reste = montant - montantPaye;
            const progress = montant > 0 ? (montantPaye / montant) * 100 : 0;
            const estPaye = reste <= 0;
            
            return (
            <div key={prestation.id} className="bg-white rounded-xl shadow p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-gray-800 text-lg">{prestation.titre}</h3>
                    {getStatusBadge(prestation.statut)}
                    {estPaye && (
                      <span className="px-2 py-1 bg-green-100 text-green-600 rounded-full text-xs font-medium">
                        💰 Payé
                      </span>
                    )}
                  </div>
                  {prestation.description && (
                    <p className="text-gray-500 text-sm mb-2">{prestation.description}</p>
                  )}
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    {prestation.client_nom && (
                      <span>👤 {prestation.client_nom}</span>
                    )}
                    <span>📅 Début: {new Date(prestation.date_debut).toLocaleDateString()}</span>
                    {prestation.date_fin && (
                      <span>📅 Fin: {new Date(prestation.date_fin).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                
                <div className="w-full md:w-48">
                  <div className="text-right mb-2">
                    <div className="text-xl font-bold text-purple-600">
                      {montant.toLocaleString()} XOF
                    </div>
                    {montant > 0 && (
                      <div className="text-sm">
                        <span className="text-green-600">{montantPaye.toLocaleString()} payé</span>
                        {reste > 0 && (
                          <span className="text-red-600 ml-2">/ {reste.toLocaleString()} reste</span>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Progress bar */}
                  {montant > 0 && (
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${estPaye ? 'bg-green-500' : 'bg-purple-500'}`}
                        style={{ width: `${Math.min(progress, 100)}%` }}
                      />
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2">
                  {/* Bouton Encaisser */}
                  {canManage && !estPaye && montant > 0 && (
                    <button
                      onClick={() => { setSelectedPrestation(prestation); setShowPaymentModal(true); }}
                      className="px-3 py-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition text-sm font-medium"
                    >
                      💰 Encaisser
                    </button>
                  )}
                  {/* Status Quick Actions */}
                  {canManage && prestation.statut === 'en_attente' && (
                    <button
                      onClick={() => handleStatusUpdate(prestation.id, 'en_cours')}
                      className="px-3 py-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition text-sm"
                    >
                      ▶️ Démarrer
                    </button>
                  )}
                  {canManage && prestation.statut === 'en_cours' && (
                    <button
                      onClick={() => handleStatusUpdate(prestation.id, 'terminee')}
                      className="px-3 py-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition text-sm"
                    >
                      ✅ Terminer
                    </button>
                  )}
                  {canManage && (
                    <button
                      onClick={() => { setEditingPrestation(prestation); setShowModal(true); }}
                      className="p-2 bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-100 transition"
                      title="Modifier"
                    >
                      ✏️
                    </button>
                  )}
                  {canManage && (
                    <button
                      onClick={() => handleDelete(prestation.id)}
                      className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                      title="Supprimer"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            </div>
            );
          })}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white rounded-xl shadow px-4 py-3 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Affichage {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredPrestations.length)} sur {filteredPrestations.length}
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ««
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  «
                </button>
                {(() => {
                  const pages = [];
                  const showPages = new Set();
                  showPages.add(1);
                  showPages.add(totalPages);
                  for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) {
                    showPages.add(i);
                  }
                  const sortedPages = [...showPages].sort((a, b) => a - b);
                  sortedPages.forEach((page, idx) => {
                    if (idx > 0 && page - sortedPages[idx - 1] > 1) {
                      pages.push(<span key={`ellipsis-${page}`} className="px-2 text-gray-400">...</span>);
                    }
                    pages.push(
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded ${currentPage === page ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                      >
                        {page}
                      </button>
                    );
                  });
                  return pages;
                })()}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  »
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  »»
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <span className="text-6xl mb-4 block">🔧</span>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {search ? 'Aucun résultat' : 'Aucune prestation'}
          </h3>
          <p className="text-gray-500 mb-4">
            {search ? 'Essayez une autre recherche' : 'Enregistrez votre première prestation'}
          </p>
          {!search && (
            <button
              onClick={() => { setEditingPrestation(null); setShowModal(true); }}
              className="px-6 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition"
            >
              Nouvelle prestation
            </button>
          )}
        </div>
      )}

      {/* Prestation Modal */}
      {showModal && (
        <PrestationModal
          prestation={editingPrestation}
          clients={clients}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            loadPrestations();
          }}
          apiBase={apiBase}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedPrestation && (
        <PrestationPaymentModal
          prestation={selectedPrestation}
          onClose={() => { setShowPaymentModal(false); setSelectedPrestation(null); }}
          onSuccess={() => {
            setShowPaymentModal(false);
            setSelectedPrestation(null);
            loadPrestations();
          }}
          apiBase={apiBase}
        />
      )}
    </div>
  );
};

// Prestation Modal Component
const PrestationModal = ({ prestation, clients, onClose, onSuccess, apiBase = '/business' }) => {
  const [form, setForm] = useState({
    titre: prestation?.titre || '',
    description: prestation?.description || '',
    client_id: prestation?.client_id || '',
    montant: prestation?.montant || '',
    date_debut: prestation?.date_debut?.split(' ')[0] || new Date().toISOString().split('T')[0],
    date_fin_prevue: prestation?.date_fin_prevue?.split(' ')[0] || '',
    statut: prestation?.statut || 'en_attente',
    notes: prestation?.notes || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (prestation) {
        await api.put(`${apiBase}/prestations/${prestation.id}`, form);
      } else {
        await api.post(`${apiBase}/prestations`, form);
      }
      onSuccess();
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">
            {prestation ? 'Modifier la prestation' : 'Nouvelle prestation'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre de la prestation *</label>
            <input
              type="text"
              value={form.titre}
              onChange={(e) => setForm({ ...form, titre: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              placeholder="Ex: Création site web, Formation..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows="2"
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              placeholder="Détails de la prestation..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
            <select
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Sans client</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.nom}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Montant (XOF)</label>
            <input
              type="number"
              min="0"
              value={form.montant}
              onChange={(e) => setForm({ ...form, montant: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              placeholder="0"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
              <input
                type="date"
                value={form.date_debut}
                onChange={(e) => setForm({ ...form, date_debut: e.target.value })}
                className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin prévue</label>
              <input
                type="date"
                value={form.date_fin_prevue}
                onChange={(e) => setForm({ ...form, date_fin_prevue: e.target.value })}
                className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
            <select
              value={form.statut}
              onChange={(e) => setForm({ ...form, statut: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="en_attente">⏳ En attente</option>
              <option value="en_cours">🔄 En cours</option>
              <option value="terminee">✅ Terminée</option>
              <option value="annulee">❌ Annulée</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows="2"
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : prestation ? 'Modifier' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Prestation Payment Modal Component
const PrestationPaymentModal = ({ prestation, onClose, onSuccess, apiBase = '/business' }) => {
  const montant = parseFloat(prestation.montant || 0);
  const montantPaye = parseFloat(prestation.montant_paye || 0);
  const reste = montant - montantPaye;
  
  const [form, setForm] = useState({
    montant: reste,
    mode_paiement: 'especes',
    reference: '',
    date_paiement: new Date().toISOString().split('T')[0]
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (parseFloat(form.montant) <= 0) {
      alert('Le montant doit être supérieur à 0');
      return;
    }
    
    setSaving(true);
    try {
      await api.post(`${apiBase}/prestations/${prestation.id}/payments`, form);
      onSuccess();
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l\'enregistrement du paiement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">💰 Encaisser un paiement</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        {/* Résumé prestation */}
        <div className="mb-4 p-4 bg-purple-50 rounded-xl">
          <div className="font-semibold text-purple-800">{prestation.titre}</div>
          {prestation.client_nom && (
            <div className="text-sm text-purple-600">👤 {prestation.client_nom}</div>
          )}
          <div className="mt-2 flex justify-between text-sm">
            <span className="text-gray-600">Total:</span>
            <span className="font-medium">{montant.toLocaleString()} XOF</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Déjà payé:</span>
            <span className="text-green-600 font-medium">{montantPaye.toLocaleString()} XOF</span>
          </div>
          <div className="flex justify-between text-sm border-t pt-2 mt-2">
            <span className="font-medium text-gray-800">Reste à payer:</span>
            <span className="text-red-600 font-bold">{reste.toLocaleString()} XOF</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Montant à encaisser *</label>
            <input
              type="number"
              min="0"
              max={reste}
              step="0.01"
              value={form.montant}
              onChange={(e) => setForm({ ...form, montant: e.target.value })}
              className="w-full border rounded-xl px-4 py-3 text-lg font-semibold focus:ring-2 focus:ring-green-500"
              required
            />
            {parseFloat(form.montant) > reste && (
              <p className="text-amber-600 text-sm mt-1">⚠️ Le montant dépasse le reste à payer</p>
            )}
            {parseFloat(form.montant) < reste && parseFloat(form.montant) > 0 && (
              <p className="text-blue-600 text-sm mt-1">ℹ️ Paiement partiel</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
            <select
              value={form.mode_paiement}
              onChange={(e) => setForm({ ...form, mode_paiement: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-green-500"
            >
              <option value="especes">💵 Espèces</option>
              <option value="mobile_money">📱 Mobile Money</option>
              <option value="virement">🏦 Virement bancaire</option>
              <option value="cheque">📝 Chèque</option>
              <option value="carte">💳 Carte bancaire</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date du paiement</label>
            <input
              type="date"
              value={form.date_paiement}
              onChange={(e) => setForm({ ...form, date_paiement: e.target.value })}
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Référence (optionnel)</label>
            <input
              type="text"
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              placeholder="N° transaction, chèque..."
              className="w-full border rounded-xl px-4 py-2 focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 font-semibold"
            >
              {saving ? 'Enregistrement...' : '💰 Encaisser'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessPrestations;
